import { SnapshotLabelHtmlClassNames } from "@/animation/video-generator";
import { ToolLoadingStateProvider } from "@/grid/ToolLoadingStateContext";
import { useExportStatus } from "@/hooks/useExportStatus";
import { useAppDispatch } from "@/reducer";
import { addPlot } from "@/reducer/PlotsState";
import type { Plot } from "@/reducer/client-models";
import { classNames } from "@/utility/jsx";
import type { GridCellLayout } from "@mm/metx-workbench.meteomatics.com";
import { Suspense, lazy, memo, useEffect } from "react";

export type GridPlotProps = {
  plot: Plot;
  cellLayout?: GridCellLayout;
};

const PlotView = memo(
  lazy(() =>
    import("@/plotView/index")
      .then(({ PlotView }) => ({ default: PlotView }))
      .catch((e) => {
        console.error("error", e);
        return { default: () => <div>Loading Plot View failed</div> };
      }),
  ),
);
const PlotOverlay = memo(
  lazy(() =>
    import("@/overlay/overlays/default/PlotOverlay")
      .then(({ DefaultPlotOverlay }) => ({ default: DefaultPlotOverlay }))
      .catch(() => {
        console.error("error");
        return { default: () => <div>Loading Plot Overlay failed</div> };
      }),
  ),
);

export function GridPlot({ plot, cellLayout }: GridPlotProps) {
  /**
   * Temporary solution to sync up react-query with redux
   */
  const dispatch = useAppDispatch();
  const { isVideoExportActive } = useExportStatus();

  useEffect(() => {
    dispatch(addPlot(plot));
  }, [dispatch, plot]);

  if (!plot) {
    return null;
  }

  return (
    <ToolLoadingStateProvider>
      <div className="overlay">
        <div className="overlay__layer">
          <Suspense fallback={<mm-loader size="large" />}>
            <PlotView plot={plot} />
          </Suspense>
        </div>
        {isVideoExportActive ? null : (
          <div className={classNames("overlay__layer", "gui-overlay", SnapshotLabelHtmlClassNames.Plot)}>
            <Suspense fallback={null}>
              <PlotOverlay plot={plot} cellLayout={cellLayout} />
            </Suspense>
          </div>
        )}
      </div>
    </ToolLoadingStateProvider>
  );
}
